import React, { useEffect, useState } from "react";
import {
  Box,
  AppBar,
  Grid,
  Breadcrumbs,
  Link,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Divider,
  Paper,
  Stack,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Navbar from "../../components/common/Navbar";
import { useStyles } from "../../assets/styles/Theme";
import { externalgetApi } from "../../services/external/externalApis";
import { ORDER_API_15 } from "../../api/ApiModel";
import {
  CON_ORTC_RETURN,
  CON_ORTC_REFUND,
  CON_ORTC_EXCHANGE,
  CON_ORTC_VOID,
  getReturnActivityLabels,
} from "../../utils/sys/Constants";
import { currencyFormatLKR } from "../../utils/common/CurrencyFormat";

// ── helpers ───────────────────────────────────────────────────────────────────

const safe = (val) => {
  if (val == null) return "—";
  if (typeof val === "object") return "—";
  const s = String(val).trim();
  return s === "" ? "—" : s;
};

const safeNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? "—" : new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

/** Selling rate from order item; return rate from return line; penalty = selling − return */
const getLineRates = (line, orderItem) => {
  const sellingRate = parseFloat(
    line.SELLINGRATE ?? line.SOLDTRATE ?? orderItem?.RATE,
  );
  const returnRate = parseFloat(line.RATE);
  const apiPenalty = parseFloat(line.PENALTYAMOUNT);
  let penalty = NaN;
  if (!Number.isNaN(apiPenalty)) {
    penalty = apiPenalty;
  } else if (!Number.isNaN(sellingRate) && !Number.isNaN(returnRate)) {
    penalty = sellingRate - returnRate;
  }
  return { sellingRate, returnRate, penalty };
};

/** Display API date values as yyyy-MM-dd */
const formatDateYmd = (val) => {
  if (val == null || String(val).trim() === "") return "—";
  const d = dayjs(val);
  return d.isValid() ? d.format("YYYY-MM-DD") : String(val).trim();
};

const getReturnTypeChip = (code, description) => {
  const c = Number(code);
  const label = typeof description === "string" && description.trim() ? description : null;
  if (c === CON_ORTC_RETURN)   return <Chip label={label || "Return"}   color="primary" size="small" />;
  if (c === CON_ORTC_REFUND)   return <Chip label={label || "Refund"}   color="success" size="small" />;
  if (c === CON_ORTC_EXCHANGE) return <Chip label={label || "Exchange"} color="warning" size="small" />;
  if (c === CON_ORTC_VOID)     return <Chip label={label || "Void"}     color="error"   size="small" />;
  return <Chip label={label || "—"} size="small" />;
};

const InfoRow = ({ label, value, theme }) => (
  <Box sx={{ display: "flex", gap: 1, py: 0.5 }}>
    <Typography
      variant="body2"
      sx={{
        color: theme.palette.text.secondary,
        minWidth: 150,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>

    <Typography
      variant="body2"
      sx={{
        color: theme.palette.text.primary,
        fontWeight: 600,
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      }}
    >
      {safe(value)}
    </Typography>
  </Box>
);

// ── component ─────────────────────────────────────────────────────────────────

function ReturnDetails() {
  const theme  = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();

  // returnRecord passed from ReturnList (has ODRETURNCODE + ORDERCODE at minimum)
  const { returnRecord } = location.state || {};

  const [returnHeader, setReturnHeader] = useState(null);   // result["4"][0]
  const [lineItems,    setLineItems]    = useState([]);      // result["2"]
  const [orderItems,   setOrderItems]   = useState([]);      // result["1"]
  const [remarkMap,    setRemarkMap]    = useState({});      // result["3"]
  const [isLoading,    setIsLoading]    = useState(true);
  const [error,        setError]        = useState(null);

  useEffect(() => {
    if (!returnRecord?.ORDERCODE || !returnRecord?.ODRETURNCODE) {
      setIsLoading(false);
      return;
    }

    const fetchDetails = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await externalgetApi(ORDER_API_15, "get", {
          ORDERCODE:    returnRecord.ORDERCODE,
          ODRETURNCODE: returnRecord.ODRETURNCODE,
        });

        // result["1"] — all order items (IDNO, MENUNAME, QTY, RATE, AMOUNT, STS …)
        const rawItems = result["1"] || [];
        setOrderItems(Array.isArray(rawItems) ? rawItems : Object.values(rawItems).flat().filter(Boolean));

        // result["2"] — return line items for this specific return
        const rawLines = result["2"] || [];
        setLineItems(Array.isArray(rawLines) ? rawLines : []);

        // result["3"] — remark lookup { ODREMARKCODE, REMARK }
        const rawRemarks = result["3"] || [];
        const rMap = {};
        (Array.isArray(rawRemarks) ? rawRemarks : []).forEach((r) => {
          rMap[r.ODREMARKCODE] = r.REMARK;
        });
        setRemarkMap(rMap);

        // result["4"] — return header (single row)
        const rawHeader = result["4"] || [];
        const header = Array.isArray(rawHeader) ? rawHeader[0] : null;
        setReturnHeader(header || null);

      } catch (err) {
        setError(err?.message || "Failed to load return details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [returnRecord?.ORDERCODE, returnRecord?.ODRETURNCODE]);

  if (!returnRecord) {
    return (
      <>
        <Navbar />
        <Box sx={{ p: 4 }}>
          <Alert severity="warning">No return record found. Please go back and select a record.</Alert>
        </Box>
      </>
    );
  }

  // Use API header data when available, fall back to navigation state
  const hdr = returnHeader || returnRecord;
  const labels = getReturnActivityLabels(
    hdr.RETURNTYPECODE ?? returnRecord.RETURNTYPECODE,
  );

  return (
    <>
      <Navbar />

      {/* ── AppBar / Breadcrumb ── */}
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer1}>
          <Grid container alignItems="center">
            <Grid item xs={12} md={8}>
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={styles.AI_breadcrumbs2}>
                <Link underline="none">Home</Link>
                <Link underline="none" sx={{ cursor: "pointer" }} onClick={() => navigate("/returnlist")}>
                  Returns
                </Link>
                <Link underline="none" sx={{ cursor: "pointer" }} onClick={() => navigate("/returnlist")}>
                  Order Activity History
                </Link>
                <Link underline="none" color="text.primary">{labels.pageTitle}</Link>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" justifyContent="flex-end" sx={styles.AI_fixedStack}>
                <Button
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/returnlist")}
                  sx={{
                    background: theme.palette.primary.main,
                    color: theme.palette.text.buttontxt,
                    textTransform: "capitalize",
                  }}
                >
                  Back
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Box sx={styles.AI_AddStyledBox}>
        {/* ── Page title bar ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
            mt: { xs: "170px", md: "155px" },
            background: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.stroke.main}`,
          }}
        >
          <ReceiptLongOutlinedIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
            {labels.pageTitle}
          </Typography>
          <Chip
            label={`# ${safe(hdr.ODRETURNCODE)}`}
            size="small"
            sx={{
              backgroundColor: `${theme.palette.primary.main}18`,
              color: theme.palette.primary.main,
              fontWeight: 600,
              fontSize: "0.8rem",
            }}
          />
          <Box sx={{ ml: "auto" }}>
            {getReturnTypeChip(hdr.RETURNTYPECODE, returnRecord.RETURNTYPEDESCRIPTION)}
          </Box>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <Box sx={{ p: { xs: 1.5, md: 2 }, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* ── Header info cards ── */}
            <Grid container spacing={2}>
              {/* Return Info — from result["4"] */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.stroke.main}`, borderRadius: 2, height: "100%" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                    {labels.sectionInfo}
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <InfoRow label={labels.refNo}   value={hdr.ODRETURNCODE}   theme={theme} />
                  <InfoRow label={labels.cnExNo}  value={hdr.RETURNNO}       theme={theme} />
                  <InfoRow label={labels.date}    value={formatDateYmd(hdr.RETURNDATE)} theme={theme} />
                  <InfoRow label={labels.amount}  value={safeNum(hdr.REFUNDAMOUNT)} theme={theme} />
               <InfoRow
  label="Remark"
  value={hdr.REMARK ? String(hdr.REMARK).slice(0, 200) : "—"}
  theme={theme}
/>
                  <InfoRow label="Entered By"    value={hdr.ENUSER}         theme={theme} />
                </Paper>
              </Grid>

              {/* Order Info — from returnRecord (list data) */}
              <Grid item xs={12} md={6}>
                <Paper elevation={0} sx={{ p: 2, border: `1px solid ${theme.palette.stroke.main}`, borderRadius: 2, height: "100%" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1.5, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                    Order Information
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  {/* <InfoRow label="Order No"      value={hdr.ORDERCODE}              theme={theme} /> */}
                  <InfoRow label="Order Code"    value={returnRecord.INVNO}         theme={theme} />
                  <InfoRow
                    label="Order Date"
                    value={formatDateYmd(returnRecord.ORDERENDATE || hdr.ORDERENDATE)}
                    theme={theme}
                  />
                  {/* <InfoRow label="Delivery Date" value={returnRecord.DELIVERYDATE}  theme={theme} /> */}
                  {/* <InfoRow label="Officer"       value={returnRecord.OFFICERNAME}   theme={theme} /> */}
                </Paper>
              </Grid>
            </Grid>

            {/* ── Returned Items table — from result["2"] ── */}
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.stroke.main}`, borderRadius: 2, overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.stroke.main}`, background: theme.palette.background.default }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: "0.7rem" }}>
                  {labels.sectionItems}
                </Typography>
              </Box>

              {lineItems.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    No line items found for this return.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                    <colgroup>
                      <col style={{ width: "36px"  }} />
                      <col style={{ width: "22%"   }} />
                      <col style={{ width: "52px"  }} />
                      <col style={{ width: "96px"  }} />
                      <col style={{ width: "96px"  }} />
                      <col style={{ width: "96px"  }} />
                      <col style={{ width: "96px"  }} />
                      <col />
                    </colgroup>
                    <TableHead>
                      <TableRow sx={{ background: theme.palette.background.tertiary }}>
                        {[
                          { label: "#",            align: "left"   },
                          { label: "Item",         align: "left"   },
                          { label: "Qty",          align: "center" },
                          { label: "Selling Rate", align: "right"  },
                          { label: "Penalty",      align: "right"  },
                          { label: labels.rate,        align: "right"  },
                          { label: "Amount",       align: "right"  },
                          { label: "Reason",       align: "left"   },
                        ].map(({ label, align }) => (
                          <TableCell
                            key={label}
                            align={align}
                            sx={{ fontWeight: 700, fontSize: "0.75rem", color: theme.palette.text.secondary, borderBottom: `1px solid ${theme.palette.stroke.main}`, whiteSpace: "nowrap", py: 1 }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((line, idx) => {
                        // Join item name from result["1"] via ORDERITEMCODE → IDNO
                        const orderItem = orderItems.find((i) => String(i.IDNO) === String(line.ORDERITEMCODE));
                        const menuName  = orderItem?.MENUNAME || safe(line.MENUCODE);
                        const remark    = remarkMap[line.ODREMARKCODE] || line.REMARK;
                        const { sellingRate, returnRate, penalty } = getLineRates(line, orderItem);
                        const penaltyDisplay =
                          !Number.isNaN(penalty) && penalty > 0 ? safeNum(penalty) : "—";

                        return (
                          <TableRow
                            key={idx}
                            sx={{ "&:last-child td": { border: 0 }, "&:hover": { background: `${theme.palette.primary.main}08` } }}
                          >
                            <TableCell align="left"   sx={{ color: theme.palette.text.secondary, fontSize: "0.8rem",  py: 1 }}>{idx + 1}</TableCell>
                            <TableCell align="left"   sx={{ fontWeight: 600, fontSize: "0.85rem", py: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 0 }}>
                              <Tooltip title={menuName !== "—" ? menuName : ""} placement="top-start">
                                <span>{safe(menuName)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: "0.85rem", py: 1 }}>{safe(line.QTY)}</TableCell>
                            <TableCell align="right"  sx={{ fontSize: "0.85rem", py: 1 }}>{safeNum(sellingRate)}</TableCell>
                            <TableCell align="right"  sx={{ fontSize: "0.85rem", py: 1 }}>{penaltyDisplay}</TableCell>
                            <TableCell align="right"  sx={{ fontSize: "0.85rem", py: 1 }}>{safeNum(returnRate)}</TableCell>
                            <TableCell align="right"  sx={{ fontWeight: 700, fontSize: "0.85rem", py: 1 }}>{safeNum(line.AMOUNT)}</TableCell>
                            <TableCell align="left"   sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary, py: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 0 }}>
                              <Tooltip title={remark !== "-" ? remark : ""} placement="top-start">
                                <span>{safe(remark)}</span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* ── Totals footer ── */}
              {lineItems.length > 0 && (
                <Box
                  sx={{
                    px: 2, py: 1.5,
                    borderTop: `1px solid ${theme.palette.stroke.main}`,
                    background: theme.palette.background.default,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 4,
                  }}
                >
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Total Qty</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {lineItems.reduce((s, i) => s + (parseFloat(i.QTY) || 0), 0)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Total Amount</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {safeNum(lineItems.reduce((s, i) => s + (parseFloat(i.AMOUNT) || 0), 0))}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>

          </Box>
        )}
      </Box>
    </>
  );
}

export default ReturnDetails;
